import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import {ButtonIcon, ButtonLink} from '@homeaway/react-buttons';
import {CHEVRON_LEFT__16, CHEVRON_RIGHT__16} from '@homeaway/svg-defs';
import {TrackingContext} from '@homeaway/react-event-tracking';
import './CardCarousel.less';
/* eslint-disable complexity */
const SETTINGS = {
    TRANSITION_DURATION_SLIDE: 350,
    TRANSITION_DURATION_SLIDE_PAGING: 500,
    DEBOUNCE_TIME: 250
};

class CardCarousel extends Component {
    static propTypes = {
        /** Text to display for the action area. */
        actionText: PropTypes.string,
        /** The url to navigate to when the action is clicked. If specified, an <a href> tag is used for the action instead of a normal button. */
        actionHref: PropTypes.string,
        /** The browsing context to display a specfied actionHref in. ie "_blank", "_parent". Default is "_self". */
        actionHrefTarget: PropTypes.string,
        /** Specifies the viewport width breakpoints and how many items to display in the carousel at each breakpoint. */
        breakpoints: PropTypes.objectOf(PropTypes.number),
        /** Specifies which card is displayed as the first visible card in the carousel. */
        cardIndex: PropTypes.number.isRequired,
        /** `getControlOffset(cardWidth, controlHeight)` Invoked on render to adjust the positioning of the carousel controls which are vertically centered by default.  Invoked with the card width and control component height in pixels. */
        getControlOffset: PropTypes.func,
        /** Determines the carousel layout alignment when 'scrolling' is 'false'. 'container' is the default where the cards and navigation buttons occupy the full width of their container and the buttons each take up 1/12 of that space. In this alignment mode the buttons will be padded from the outer edges of the carousel container. 'cards' specifies that the cards use the full width of the container and the navigation buttons are absolutely positioned outside of the container. 'cardsWithOverlapButtons' is just like card, but the buttons positions are modified so that the buttons partially overlap the outer cards. 'buttons' specifies that the cards and buttons take up the full width of the container and the outer edge of the buttons lines up with the outer edges of the container. */
        layoutAlignment: PropTypes.oneOf([
            'container',
            'cards',
            'cardsWithOverlapButtons',
            'buttons'
        ]),
        /**  The translated label for the "next" control button. */
        nextLabel: PropTypes.string,
        /** `onActionClick(event)` Triggered when the `actionText` is clicked. Passes the click event. */
        onActionClick: PropTypes.func,
        /** `onCardsShownChange(newCardIndex, allIndicesInView)` Triggered when the cards shown in the carousel change. Passes the new `cardIndex` value and all the card indices in view. */
        onCardsShownChange: PropTypes.func,
        /** `onNextClick(event, nextCardIndex)` Triggered when the "next" control button is clicked. Passes the click event and the proposed new `cardIndex` value. */
        onNextClick: PropTypes.func.isRequired,
        /** `onPreviousClick(event, nextCardIndex)` Triggered when the "previous" control button is clicked. Passes the click event and the proposed new `cardIndex` value. */
        onPreviousClick: PropTypes.func.isRequired,
        /** `onResize({numberCardsVisible, numberChildren, breakpoints, needsControls})` Triggered when the browser resizes. */
        onResize: PropTypes.func,
        /** `onScroll(newCardIndex)` Triggered when the carousel is scrolled. Passes the proposed new `cardIndex` value. */
        onScroll: PropTypes.func,
        /** Adjusts the `nextCardIndex` passed to `onNextClick` and `onPreviousClick`. If `true` the index will be adjusted based on the number of items visible in the carousel. If false, the index is adjusted by 1 item. */
        paging: PropTypes.bool,
        /** The translated label for the "previous" control button. */
        previousLabel: PropTypes.string,
        /** Hides the navigation controls and allows free horizontal scrolling. */
        scrolling: PropTypes.bool,
        /**  offset margin for scrolling from edge to edge of viewport. */
        scrollMarginOffset: PropTypes.number,
        /** When scrolling is enabled, determines the percentage of "card peek", a UX pattern to indicate additional content is available for scrolling. Accepts a value of 0.2 or 0.5 which represents 20% and 50% peek respectively. */
        scrollingPeekPct: PropTypes.oneOf([0.2, 0.5]),
        /** The subheading text under the title */
        subheading: PropTypes.string,
        /** The text or html to display as the Title */
        title: PropTypes.node
    };

    static defaultProps = {
        breakpoints: {
            '1200': 4,
            '992': 3,
            '768': 4,
            '420': 3,
            '0': 2
        },
        cardIndex: 0,
        layoutAlignment: 'container',
        scrollMarginOffset: 0,
        nextLabel: 'Next',
        onCardsShownChange: () => {},
        onNextClick: () => {},
        onPreviousClick: () => {},
        onResize: () => {},
        onScroll: () => {},
        paging: true,
        previousLabel: 'Previous',
        scrolling: false,
        scrollingPeekPct: 0.2
    };

    constructor(props) {
        super(props);

        this.controlHeight = 48;
        this.timeouts = {};

        this.state = {
            cardCount: this.getCardCountFromBreakpoint(0),
            carouselState: '',
            viewboxTransform: 'none'
        };

        this.debouncedWindowResize = debounce(
            this.handleWindowResize,
            SETTINGS.DEBOUNCE_TIME
        );
        this.handleScrollDebounced = debounce(
            this.handleScroll,
            SETTINGS.DEBOUNCE_TIME
        );
        // card indices shown. track so that when it changes, onCardsShownChange callback is invoked with new index and indices shown
        this.shownIndices = [];
        // scroll index tracked here. it should equal the card index when passed as a prop, but is not tracked when actively scrolling.
        this.scrollIndex = this.props.cardIndex;
        // this only changes on mount or resize, so cache it whenever invoking this.getCardWidth
        this.cardWidth = 0;
        // used to determine if the keyboard navigation is moving forward or backward
        this.lastFocusedCardIndex = 0;
    }

    componentDidMount() {
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.debouncedWindowResize);
            const {cardCount} = this.updateBasedOnComponentWidth(true);
            const {scrolling, cardIndex} = this.props;
            // componentDidUpdate will handle setting initial shown indices for click mode.
            // For scrolling mode, this initial setting will get done in the scroll handler
            // invoked by the forced scrollTo call from this.updateBasedOnComponentWidth(true).
            // If the index is 0, however, the scroll handler will not get invoked because the
            // final scrollLeft will be the same as the initially rendered scrolLeft (0px), and
            // thus we should call prop.onCardsShownChange here to set the initial shown indices
            if (scrolling && cardIndex === 0) {
                const {
                    activeIndex,
                    shownIndices
                } = this.getShownIndicesForScroll(cardCount);
                this.context.trigger('ux.cards.presented', {
                    card_indices: shownIndices.join(',')
                });
                this.props.onCardsShownChange(activeIndex, shownIndices);
                this.shownIndices = shownIndices;
            }
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const {cardIndex, scrolling} = this.props;
        const {cardCount} = this.state;
        const cardIndexChanged = prevProps && prevProps.cardIndex !== cardIndex;
        const scrollingStateChanged =
            prevProps && prevProps.scrolling !== scrolling;
        const cardCountChanged = prevState && prevState.cardCount !== cardCount;

        // if index changed on props, slide to new starting index
        const prevIndexNormalized = this.normalizeIndex(prevProps.cardIndex);
        const newIndexNormalized = this.normalizeIndex(cardIndex);

        if (prevIndexNormalized !== newIndexNormalized) {
            this.scrollIndex = newIndexNormalized;
            this.slide(prevProps.cardIndex, newIndexNormalized);
        }

        const viewboxTransform = this.getViewboxPos(
            this.getOffsetByIndex(cardIndex)
        );
        if (viewboxTransform !== this.state.viewboxTransform) {
            this.setState({viewboxTransform});
        }

        // If transitioned from scrolling to non-scrolling, reset the scrollLeft
        // position.
        if (!scrolling && scrollingStateChanged && this.container) {
            this.container.scrollLeft = 0;
        }

        if (scrolling && cardIndexChanged) {
            this.scrollTo(cardIndex);
        }

        // If active card index or card count changes while in click mode,
        // update this.shownIndices and invoke onCardsShownChange callback.
        // This is triggered by next/back click or resize that changes
        // card count or changes from scrolling to click mode.
        // In scrolling mode, handleScrollDebounce invokes this notification
        // because componentDidUpdate is not triggered because no state
        // or props change. This would be different if the cardIndex prop
        // was controlled by the consumer in scroll mode (like it is in click mode)
        if (!scrolling) {
            const {activeIndex, shownIndices} = this.getShownIndicesForClick(
                cardIndex,
                cardCount
            );
            if (
                cardIndexChanged ||
                cardCountChanged ||
                !this.indicesArraysAreEqual(shownIndices, this.shownIndices)
            ) {
                this.context.trigger('ux.cards.presented', {
                    card_indices: shownIndices.join(',')
                });
                this.props.onCardsShownChange(activeIndex, shownIndices);
                this.shownIndices = shownIndices;
            }
        }
    }

    componentWillUnmount() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.debouncedWindowResize);
        }
    }

    static contextType = TrackingContext;

    indicesArraysAreEqual(a1, a2) {
        return a1.length === a2.length && a1.toString() === a2.toString();
    }

    updateBasedOnComponentWidth(force) {
        const viewboxTransform = this.getViewboxPos(
            this.getOffsetByIndex(this.props.cardIndex)
        );
        const cardCount = this.getCardCountFromBreakpoint(
            this.state.cardCount,
            force
        );

        this.handleOnResizeCallback(cardCount);
        this.cardWidthPx = this.getCardWidthPx(cardCount);

        if (
            this.props.scrolling &&
            ((this.props.cardIndex >= 0 &&
                cardCount !== this.state.cardCount) ||
                force)
        ) {
            this.scrollTo(this.props.cardIndex, cardCount);
        }

        this.setState({
            viewboxTransform,
            cardCount
        });

        return {cardCount};
    }

    handleOnResizeCallback(cardCount) {
        const needsControls = this.getAreControlsNecessary(cardCount);
        const breakpoints = this.props.breakpoints
            ? this.sortBreakpoints(this.props.breakpoints)
            : 'none';
        const numberChildren = this.props.children
            ? this.props.children.length
            : 0;

        this.props.onResize({
            numberCardsVisible: cardCount,
            numberCardsTotal: numberChildren,
            breakpoints,
            needsControls
        });
    }

    handleWindowResize = () => {
        this.updateBasedOnComponentWidth();
    };

    /** Returns a boolean indicating the display of fwd/back nav arrows  */
    getAreControlsNecessary(cardCount) {
        return this.props.children && this.props.children.length > cardCount;
    }

    getCardCountFromBreakpoint(cardCount = 0, forceSetState) {
        const {breakpoints} = this.props;
        const width = this.content ? this.content.clientWidth : 0;
        let i;
        let bp;
        let parsedBp;
        const sortedBreakpoints = this.sortBreakpoints(breakpoints);

        for (i = 0; i < sortedBreakpoints.length; i++) {
            bp = sortedBreakpoints[i];
            parsedBp = parseInt(bp, 10);
            if (width >= parsedBp) {
                const newCardCount = breakpoints[bp];
                if (newCardCount !== cardCount || forceSetState) {
                    cardCount = newCardCount;
                }
                break;
            }
        }
        return cardCount;
    }

    getOffsetByIndex(index) {
        const normalizedIndex = this.normalizeIndex(index);
        const cardWidth = this.getCardWidthPct(this.state.cardCount);
        return cardWidth * normalizedIndex * 100;
    }

    getViewboxPos(pct) {
        if (this.container) {
            return this.props.scrolling
                ? 'translateX(0%)'
                : `translateX(-${pct}%)`;
        }

        return 'none';
    }

    normalizeIndex(index) {
        const {cardCount} = this.state;
        const {scrolling} = this.props;
        const newCount = scrolling ? cardCount - 1 : cardCount;
        const maxIndex = this.props.children
            ? this.props.children.length - newCount
            : 0;
        if (!index || index < 0) {
            return 0;
        } else if (index > maxIndex) {
            return maxIndex;
        }
        return index;
    }

    handleActionClick = (e) => {
        this.context.trigger('card.action.selected');
        if (this.props.onActionClick) {
            this.props.onActionClick(e);
        }
    };

    handleCardClick = (evt = {currentTarget: {dataset: {index: '-1'}}}) => {
        const index = parseInt(evt.currentTarget.dataset.index, 10);
        this.context.trigger('card.selected', {card_index: `${index}`});
    };

    handlePrevNext = (evt, next, index) => {
        // Explicit index is used if passed in. Otherwise calculate based on
        // current cardIndex and the number of cards displayed.
        index =
            typeof index === 'number'
                ? this.normalizeIndex(index)
                : this.getNewIndex(next);
        this.context.trigger('ux.cards_visible.adjusted');
        if (next) {
            this.props.onNextClick(evt, index);
        } else {
            this.props.onPreviousClick(evt, index);
        }
    };

    handleNext = (evt) => {
        this.handlePrevNext(evt, true);
    };

    handlePrevious = (evt) => {
        this.handlePrevNext(evt, false);
    };

    handleScroll = () => {
        const {activeIndex, shownIndices} = this.getShownIndicesForScroll();
        this.props.onScroll(activeIndex);
        if (
            activeIndex !== this.scrollIndex ||
            !this.indicesArraysAreEqual(shownIndices, this.shownIndices)
        ) {
            this.context.trigger('ux.cards_visible.adjusted');
            this.context.trigger('ux.cards.presented', {
                card_indices: shownIndices.join(',')
            });
            this.props.onCardsShownChange(activeIndex, shownIndices);
        }
        this.shownIndices = shownIndices;
        this.scrollIndex = activeIndex;
    };

    handleScrollWhenNav = () => {
        // When using a non-scrolling carousel (nav buttons) and using keyboard
        // navigation, if the user focuses a card beyond the currently visible
        // cards, the browser will automatically scroll the card into view. This
        // caused several issues (animation, inconsistent placing). So, on scroll,
        // reset the scroll position.
        this.container.scrollLeft = 0;
    };

    // This handler is used to control the keyboard navigation behavior.
    // The focussed index is compared with last focussed to determine which
    // direction user is moving and then if navigating beyond currently visible
    // cards the next index is calculated.
    handleCardFocus = (evt = {currentTarget: {dataset: {index: '0'}}}) => {
        const {cardIndex} = this.props;
        const {cardCount} = this.state;
        const focusedCardIndex = parseInt(evt.currentTarget.dataset.index, 10);
        const isMovingForward = focusedCardIndex > this.lastFocusedCardIndex;
        const focusPrev = focusedCardIndex < cardIndex;
        const focusNext = focusedCardIndex > cardIndex + cardCount - 1;
        this.lastFocusedCardIndex = focusedCardIndex;

        if (focusNext || focusPrev) {
            // If the focussed card is more than a page away (forward or back),
            // request it explicitly. Otherwise, let handlePrevNext calculate
            // the next index.
            const index =
                focusedCardIndex > cardIndex + cardCount ||
                focusedCardIndex < cardIndex - cardCount
                    ? focusedCardIndex
                    : null;
            this.handlePrevNext(evt, isMovingForward, index);
        }
    };

    slide(oldIndex, newIndex) {
        const {paging} = this.props;
        const viewboxTransform = this.getViewboxPos(
            this.getOffsetByIndex(newIndex)
        );

        const state = {
            viewboxTransform,
            carouselState: paging ? 'paging' : 'animating'
        };

        this.setState(state, () => {
            clearTimeout(this.timeouts.slide);
            this.timeouts.slide = setTimeout(
                () => {
                    delete this.timeouts.slide;
                    this.setState({
                        carouselState: ''
                    });
                },
                paging
                    ? SETTINGS.TRANSITION_DURATION_SLIDE_PAGING
                    : SETTINGS.TRANSITION_DURATION_SLIDE
            );
        });
    }

    sortBreakpoints(breakpoints = {}) {
        return Object.keys(breakpoints).sort((a, b) => {
            const parsedA = parseInt(a, 10);
            const parsedB = parseInt(b, 10);

            if (parsedA === parsedB) {
                return 0;
            }
            return parsedA > parsedB ? -1 : 1;
        });
    }

    getNewIndex(isNext) {
        const {paging, cardIndex} = this.props;
        const {cardCount} = this.state;
        let cardsPerNav = paging ? cardCount - 1 || 1 : 1;
        const newIndex = isNext
            ? cardIndex + cardsPerNav
            : cardIndex - cardsPerNav;
        return this.normalizeIndex(newIndex);
    }

    getCardWidthPx(cardCountOverride) {
        const {cardCount} = this.state;
        const newCardCount = cardCountOverride || cardCount;
        const {scrolling, scrollingPeekPct} = this.props;
        const containerWidth = this.container
            ? this.container.clientWidth - this.viewbox.offsetLeft
            : 0;
        const cardWidth =
            containerWidth > 0 && newCardCount > 0
                ? containerWidth /
                  (newCardCount - (scrolling ? 1 - scrollingPeekPct : 0))
                : 0;
        this.cardWidth = cardWidth;
        return cardWidth;
    }

    getCardWidthPct(cardCount) {
        const {scrolling, scrollingPeekPct} = this.props;

        return cardCount > 0
            ? 1 / (cardCount - (scrolling ? 1 - scrollingPeekPct : 0))
            : 0;
    }

    setContent = (content) => {
        this.content = content;
    };

    setContainer = (container) => {
        this.container = container;
    };

    setViewbox = (viewbox) => {
        this.viewbox = viewbox;
    };

    calcControlOffset() {
        // If a method to get the control offset was specified and the container
        // has been rendered with some width (not SSR) then get the offset through
        // the function. Otherwise, return -1 so the render will simply center
        // the controls. This minimizes shifting difference between server/client
        // rendering.
        // Note: 8 is a hard coded value for the padding of the card container in order
        // to prevent calculating this dynamically.
        return this.props.getControlOffset &&
            this.container &&
            this.container.clientWidth
            ? this.props.getControlOffset(
                  this.getCardWidthPx() - 8,
                  this.controlHeight
              )
            : -1;
    }

    curve(x) {
        return -2 * Math.pow(x, 3) + 3 * Math.pow(x, 2);
    }

    getShownIndicesForClick(cardIndex, cardCount) {
        const shownIndices = [];
        for (let i = 0; i < cardCount; i++) {
            shownIndices.push(cardIndex + i);
        }
        return {
            activeIndex: cardIndex,
            shownIndices
        };
    }

    getShownIndicesForScroll(cardCount = this.state.cardCount) {
        const {scrollingPeekPct} = this.props;
        const cardWidth = this.cardWidth;
        // Calculate the margin between cards (16px) as a percentage relative to the card width.
        // Because all cards have 8px padding on each side (16px total between card), this marginPct
        // is used to actually determine when the card content is in/out of view, not just the padding
        const marginPct = 16 / cardWidth;
        const rawIndex = this.container.scrollLeft / cardWidth;
        const activeIndex = Math.abs(Math.ceil(rawIndex - marginPct));
        const shownIndices = [];
        const scrolledPct = rawIndex - Math.floor(rawIndex);
        const startingIndex =
            scrolledPct > marginPct ? activeIndex - 1 : activeIndex;
        const extraCardCount =
            scrolledPct >= 1 - scrollingPeekPct + marginPct / 2 ? 1 : 0;
        const indicesShownCount = cardCount + extraCardCount;
        for (let i = 0; i < indicesShownCount; i++) {
            shownIndices.push(startingIndex + i);
        }
        return {activeIndex, shownIndices};
    }

    scrollTiming(time, target, origin, range) {
        const done = time >= SETTINGS.TRANSITION_DURATION_SLIDE;
        const position = done
            ? target
            : origin +
              range * this.curve(time / SETTINGS.TRANSITION_DURATION_SLIDE);
        return {done, position};
    }

    scrollTo(cardIndex, cardCount) {
        if (this.animating || typeof window === 'undefined') {
            return;
        }
        const normalizedIndex = this.normalizeIndex(cardIndex);
        const cardWidth = this.getCardWidthPx(cardCount);

        const slide = Math.round(cardWidth * normalizedIndex);

        this.animating = true;
        let start;
        const element = this.container;
        const origin = element.scrollLeft;
        const range = slide - origin;

        const step = (timestamp) => {
            if (!start) {
                start = timestamp;
            }
            const {position, done} = this.scrollTiming(
                timestamp - start,
                slide,
                origin,
                range
            );
            element.scrollLeft = position;

            if (done) {
                this.animating = false;
            } else {
                window.requestAnimationFrame(step);
            }
        };

        window.requestAnimationFrame(step);
    }

    renderChildren(pct = 0) {
        const {children, scrolling} = this.props;
        return React.Children.map(children, (child, index) => {
            const id = `card-${index}`;
            return (
                <div
                    className="CardCarousel__card-container"
                    style={{width: `${pct * 100}%`}}
                    key={index}
                    data-id={id}
                    data-index={index}
                    onClick={this.handleCardClick}
                    // Only tracking focus for navigation of non-scrolling carousel.
                    {...(!scrolling && {onFocus: this.handleCardFocus})}
                    ref={(ref) => {
                        /* istanbul ignore next */
                        this[id] = ref;
                    }}
                >
                    {child}
                </div>
            );
        });
    }

    render() {
        const {
            actionHref,
            actionHrefTarget,
            actionText,
            cardIndex,
            children,
            onActionClick,
            scrolling,
            title,
            subheading,
            layoutAlignment,
            scrollMarginOffset
        } = this.props;

        const {carouselState, cardCount, viewboxTransform} = this.state;
        const normalizedIndex = this.normalizeIndex(cardIndex);
        const isAnimating =
            carouselState === 'animating' || carouselState === 'paging';
        const hasAction = (onActionClick || actionHref) && actionText;
        const hasHeader = title || hasAction;
        const hasSubheading = title && subheading;
        const hasPrev = normalizedIndex > 0;
        const hasNext = children
            ? normalizedIndex < children.length - cardCount
            : false;
        const controlOffset = this.calcControlOffset();
        const hasControlOffset = controlOffset >= 0;
        const needsControls = this.getAreControlsNecessary(cardCount);
        const cardWidth = this.getCardWidthPct(cardCount);

        const headerClasses = classnames('CardCarousel__header', {
            'CardCarousel__header--scrolling': scrolling,
            'CardCarousel__header--not-scrolling': !scrolling
        });

        const titleClasses = classnames('CardCarousel__title', {
            'CardCarousel__title--scrolling': scrolling
        });

        const navPrevClasses = classnames(
            'CardCarousel__nav CardCarousel__nav-prev',
            {
                'CardCarousel__nav--disabled': !hasPrev,
                'CardCarousel__nav--no_offset': !hasControlOffset,
                'CardCarousel__nav-prev--overlap-card':
                    layoutAlignment === 'cardsWithOverlapButtons'
            }
        );

        const navNextClasses = classnames(
            'CardCarousel__nav CardCarousel__nav-next',
            {
                'CardCarousel__nav--disabled': !hasNext,
                'CardCarousel__nav--no_offset': !hasControlOffset,
                'CardCarousel__nav-next--overlap-card':
                    layoutAlignment === 'cardsWithOverlapButtons'
            }
        );

        const navStyle = hasControlOffset
            ? {
                  paddingTop: controlOffset
              }
            : {};

        const viewboxClasses = classnames('CardCarousel__viewbox', {
            'CardCarousel__viewbox--animating': isAnimating,
            'CardCarousel__viewbox--paging': carouselState === 'paging',
            'CardCarousel__viewbox--scrolling': scrolling
        });

        const viewboxStyles = viewboxTransform
            ? {transform: viewboxTransform}
            : {transform: 'none'};

        let contentStyles = {};
        if (scrolling && scrollMarginOffset < 0) {
            viewboxStyles.marginLeft = `${-scrollMarginOffset + 8}px`;
            contentStyles = {
                width: 'auto',
                marginLeft: `${scrollMarginOffset}px`,
                marginRight: `${scrollMarginOffset}px`
            };
        }

        const rootClasses = classnames('CardCarousel', {
            'CardCarousel--layout-alignment-cards':
                (layoutAlignment === 'cards' ||
                    layoutAlignment === 'cardsWithOverlapButtons') &&
                !scrolling,
            'CardCarousel--layout-alignment-buttons':
                layoutAlignment === 'buttons' && !scrolling
        });

        const containerClasses = classnames('CardCarousel__container', {
            'CardCarousel__container--scrolling': scrolling
        });

        const titleElement = title && (
            <div className={`${titleClasses} h2`} role="heading" aria-level="2">
                {title}
            </div>
        );

        const buttonLink = hasAction && (
            <ButtonLink
                className={classnames('CardCarousel__action', {
                    'CardCarousel__subheading-action': hasSubheading,
                    'CardCarousel__no-subheading-action': !hasSubheading
                })}
                href={actionHref}
                target={actionHrefTarget}
                onClick={this.handleActionClick}
                label={actionText}
                size="sm"
            />
        );

        return (
            <div className={rootClasses}>
                {hasHeader && (
                    <div className={headerClasses}>
                        {hasSubheading && (
                            <div className="CardCarousel__title-container">
                                {titleElement}
                                <div className="CardCarousel__subheading-container">
                                    <p className="CardCarousel__subheading">
                                        {subheading}
                                    </p>
                                    <div className="CardCarousel__action-container">
                                        {buttonLink}
                                    </div>
                                </div>
                            </div>
                        )}
                        {!hasSubheading && titleElement}
                        {!hasSubheading && buttonLink}
                    </div>
                )}
                <div
                    className="CardCarousel__content"
                    ref={this.setContent}
                    style={contentStyles}
                >
                    {!scrolling && (
                        <div
                            className={navPrevClasses}
                            onClick={this.handlePrevious}
                            tabIndex={-1}
                            style={navStyle}
                            role="presentation"
                        >
                            {needsControls && (
                                <div>
                                    <ButtonIcon
                                        label={this.props.previousLabel}
                                        circle
                                        svgMarkup={CHEVRON_LEFT__16}
                                        aria-label={this.props.previousLabel}
                                        disabled={!hasPrev}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <div
                        className={containerClasses}
                        ref={this.setContainer}
                        onScroll={
                            scrolling
                                ? this.handleScrollDebounced
                                : this.handleScrollWhenNav
                        }
                    >
                        <div
                            className={viewboxClasses}
                            style={viewboxStyles}
                            ref={this.setViewbox}
                        >
                            {this.renderChildren(cardWidth)}
                        </div>
                    </div>
                    {!scrolling && (
                        <div
                            className={navNextClasses}
                            onClick={this.handleNext}
                            tabIndex={-1}
                            style={navStyle}
                            role="presentation"
                        >
                            {needsControls && (
                                <div>
                                    <ButtonIcon
                                        label={this.props.nextLabel}
                                        circle
                                        svgMarkup={CHEVRON_RIGHT__16}
                                        aria-label={this.props.nextLabel}
                                        disabled={!hasNext}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default CardCarousel;
